/* Native scrolling shared by product tracks and the mobile brand grid. */
class HearthCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.track = this.querySelector('[data-carousel-track]') || document.getElementById(this.dataset.trackId);
    if (!this.track) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.previous = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.controls = this.querySelector('[data-carousel-controls]');
    this.progress = this.querySelector('[data-carousel-progress]');
    this.update = () => {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.max(0, Math.min(max, this.track.scrollLeft));
      const overflow = max > 2;
      if (this.controls) this.controls.hidden = !overflow;
      if (this.progress) {
        this.progress.hidden = !overflow;
        this.progress.setAttribute('aria-valuenow', String(max ? Math.round(position / max * 100) : 0));
        const indicator = this.progress.firstElementChild;
        indicator.style.width = `${this.track.clientWidth / Math.max(1, this.track.scrollWidth) * 100}%`;
        indicator.style.marginLeft = `${position / Math.max(1, this.track.scrollWidth) * 100}%`;
      }
      if (this.previous) this.previous.disabled = !overflow || position < 2;
      if (this.next) this.next.disabled = !overflow || position >= max - 2;
    };
    this.move = direction => {
      const card = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * ((card?.getBoundingClientRect().width || this.track.clientWidth) + gap),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    this.previous?.addEventListener('click', () => this.move(-1), { signal });
    this.next?.addEventListener('click', () => this.move(1), { signal });
    this.track.addEventListener('scroll', this.update, { passive: true, signal });
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key.startsWith('Arrow')) this.move(event.key === 'ArrowLeft' ? -1 : 1);
      else this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: 'instant' });
    }, { signal });
    // Opt-in mouse dragging; keep touch scrolling and ordinary link clicks native.
    if (this.hasAttribute('data-mouse-drag')) {
      let drag = null, suppressClick = false;
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        suppressClick = false;
        drag = { id:event.pointerId, x:event.clientX, left:this.track.scrollLeft, active:false };
      }, { signal });
      this.track.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const distance = event.clientX - drag.x;
        if (!drag.active && Math.abs(distance) > 6) {
          drag.active = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        if (drag.active) { event.preventDefault(); this.track.scrollLeft = drag.left - distance; }
      }, { signal });
      const endDrag = event => {
        if (!drag || event.pointerId !== drag.id) return;
        suppressClick = drag.active;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-dragging');
        drag = null;
      };
      window.addEventListener('pointerup', endDrag, { signal });
      window.addEventListener('pointercancel', endDrag, { signal });
      this.track.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.track.addEventListener('click', event => {
        if (suppressClick && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
      }, { capture:true, signal });
    }
    this.resize = new ResizeObserver(this.update);
    this.resize.observe(this.track);
    this.mutations = new MutationObserver(this.update);
    this.mutations.observe(this.track, { childList: true });
    this.update();
  }
  disconnectedCallback() {
    this.controller?.abort(); this.resize?.disconnect(); this.mutations?.disconnect();
    this.controller = null;
  }
}
if (!customElements.get('ho-carousel')) customElements.define('ho-carousel', HearthCarousel);
